import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input } from '@artosera/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  centimesDepuisEuros,
  schemaVersionPrixAddon,
  type ModuleAddon,
  type VersionPrixAddon,
} from '@artosera/shared';
import { usePeut } from '../auth/usePeut.js';
import { marketplaceConsoleApi, type PrixAddonVue } from '../lib/addons.js';
import { modeDemo } from '../lib/api.js';
import styles from '../styles/form.module.css';

export function PrixAddon({
  addon,
  ligne,
}: {
  addon: ModuleAddon;
  ligne: PrixAddonVue | undefined;
}) {
  const { t, i18n } = useTranslation('console');
  const peut = usePeut();
  const client = useQueryClient();
  const [geste, setGeste] = useState<'ajout' | number | null>(null);
  const [mensuel, setMensuel] = useState('');
  const [annuel, setAnnuel] = useState('');
  const [achat, setAchat] = useState('');
  const [essai, setEssai] = useState('');
  const [depuisLe, setDepuisLe] = useState('');
  const [motif, setMotif] = useState('');
  const [invalide, setInvalide] = useState(false);
  const cle = ['console', 'addons', 'prix'];
  const mutation = useMutation({
    mutationFn: (corps: unknown) =>
      geste === 'ajout'
        ? marketplaceConsoleApi.ajouterPrix(addon, corps)
        : marketplaceConsoleApi.supprimerPrix(addon, geste as number, {
            motif,
            revision: ligne?.revision ?? 0,
          }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: cle });
      setGeste(null);
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: cle });
    },
  });
  const date = (valeur: string | Date) => new Date(valeur).toLocaleString(i18n.language);
  const montant = (centimes: number | null) =>
    centimes === null
      ? t('addons.prix.nonVendue')
      : new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(
          centimes / 100,
        );
  function version(prix: VersionPrixAddon) {
    return (
      <li key={prix.version}>
        <p>
          {t('addons.prix.version', { version: prix.version })} · {date(prix.depuisLe)}
        </p>
        <p>
          {t('addons.prix.mensuel')} : {montant(prix.mensuelHtCentimes)} · {t('addons.prix.annuel')}{' '}
          : {montant(prix.annuelHtCentimes)} · {t('addons.prix.achat')} :{' '}
          {montant(prix.achatHtCentimes)}
        </p>
        <p>
          {t('addons.prix.essaiValeur', { jours: prix.essaiJours })} · {prix.motif}
        </p>
        {ligne?.futures.some((future) => future.version === prix.version) && modifiable ? (
          <Button onClick={() => ouvrir(prix.version)}>{t('addons.prix.supprimer')}</Button>
        ) : null}
      </li>
    );
  }
  function ouvrir(prochain: 'ajout' | number) {
    mutation.reset();
    setInvalide(false);
    setGeste(prochain);
    setMotif('');
    setMensuel('');
    setAnnuel('');
    setAchat('');
    setEssai('');
    setDepuisLe('');
  }
  const modifiable = peut('console:manage-addon-prices') && !modeDemo();
  const historique =
    ligne?.versions.filter(
      (prix) =>
        prix.version !== ligne.enVigueur?.version &&
        !ligne.futures.some((future) => future.version === prix.version),
    ) ?? [];
  return (
    <section>
      <h4>{t('addons.prix.titre')}</h4>
      <h5>{t('addons.prix.enVigueur')}</h5>
      {ligne?.enVigueur ? <ul>{version(ligne.enVigueur)}</ul> : <p>{t('addons.aucun')}</p>}
      {ligne?.futures.length ? (
        <>
          <h5>{t('addons.prix.futures')}</h5>
          <ul>{ligne.futures.map(version)}</ul>
        </>
      ) : null}
      <details>
        <summary>{t('addons.prix.historique')}</summary>
        <ul>{historique.map(version)}</ul>
      </details>
      {modifiable ? (
        <Button onClick={() => ouvrir('ajout')}>{t('addons.prix.ajouter')}</Button>
      ) : null}
      <Dialog
        open={geste !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert && !mutation.isPending) setGeste(null);
        }}
        title={t(geste === 'ajout' ? 'addons.prix.ajouter' : 'addons.prix.confirmerSuppression')}
        closeLabel={t('addons.annuler')}
      >
        <form
          className={styles.champ}
          onSubmit={(event) => {
            event.preventDefault();
            if (mutation.isPending) return;
            if (geste !== 'ajout') {
              mutation.mutate({});
              return;
            }
            try {
              const corps = schemaVersionPrixAddon.parse({
                mensuelHtCentimes: mensuel.trim() ? centimesDepuisEuros(mensuel) : null,
                annuelHtCentimes: annuel.trim() ? centimesDepuisEuros(annuel) : null,
                achatHtCentimes: achat.trim() ? centimesDepuisEuros(achat) : null,
                essaiJours: Number(essai),
                depuisLe: new Date(depuisLe).toISOString(),
                motif,
                revision: ligne?.revision ?? 0,
              });
              setInvalide(false);
              mutation.mutate(corps);
            } catch {
              setInvalide(true);
            }
          }}
        >
          {geste === 'ajout' ? (
            <>
              <p>{t('addons.prix.aide')}</p>
              {(
                [
                  ['mensuel', mensuel, setMensuel],
                  ['annuel', annuel, setAnnuel],
                  ['achat', achat, setAchat],
                ] as const
              ).map(([nom, valeur, changer]) => (
                <label key={nom}>
                  {t(`addons.prix.${nom}`)}
                  <Input
                    inputMode="decimal"
                    value={valeur}
                    onChange={(e) => changer(e.target.value)}
                  />
                </label>
              ))}
              <label>
                {t('addons.prix.essai')}
                <Input
                  required
                  type="number"
                  min={0}
                  max={90}
                  step={1}
                  value={essai}
                  onChange={(e) => setEssai(e.target.value)}
                />
              </label>
              <label>
                {t('addons.prix.depuisLe')}
                <Input
                  required
                  type="datetime-local"
                  step={1}
                  value={depuisLe}
                  onChange={(e) => setDepuisLe(e.target.value)}
                />
              </label>
            </>
          ) : (
            <p>{t('addons.prix.version', { version: geste })}</p>
          )}
          <label>
            {t('addons.motif')}
            <Input
              required
              maxLength={500}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
          </label>
          {invalide ? <p role="alert">{t('addons.prix.invalide')}</p> : null}
          {mutation.isError ? <p role="alert">{t('addons.echec')}</p> : null}
          <Button type="submit" disabled={mutation.isPending || !motif.trim()}>
            {t(geste === 'ajout' ? 'addons.enregistrer' : 'addons.prix.supprimer')}
          </Button>
        </form>
      </Dialog>
    </section>
  );
}
